import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { organizationAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowRight, ArrowLeft, Check, Search, Building2, User, Briefcase, Store, Heart, Users, Upload, Sparkles, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';

const COUNTRIES = [
  { code: 'CH', name: 'Switzerland', currency: 'CHF', tax: 'MwSt 8.1%' },
  { code: 'US', name: 'United States', currency: 'USD', tax: 'Sales Tax' },
  { code: 'GB', name: 'United Kingdom', currency: 'GBP', tax: 'VAT 20%' },
  { code: 'DE', name: 'Germany', currency: 'EUR', tax: 'MwSt 19%' },
  { code: 'FR', name: 'France', currency: 'EUR', tax: 'TVA 20%' },
  { code: 'ES', name: 'Spain', currency: 'EUR', tax: 'IVA 21%' },
  { code: 'IT', name: 'Italy', currency: 'EUR', tax: 'IVA 22%' },
  { code: 'PT', name: 'Portugal', currency: 'EUR', tax: 'IVA 23%' },
  { code: 'NL', name: 'Netherlands', currency: 'EUR', tax: 'BTW 21%' },
  { code: 'BE', name: 'Belgium', currency: 'EUR', tax: 'BTW 21%' },
  { code: 'AT', name: 'Austria', currency: 'EUR', tax: 'USt 20%' },
  { code: 'IE', name: 'Ireland', currency: 'EUR', tax: 'VAT 23%' },
  { code: 'SE', name: 'Sweden', currency: 'SEK', tax: 'Moms 25%' },
  { code: 'DK', name: 'Denmark', currency: 'DKK', tax: 'Moms 25%' },
  { code: 'NO', name: 'Norway', currency: 'NOK', tax: 'MVA 25%' },
  { code: 'FI', name: 'Finland', currency: 'EUR', tax: 'ALV 25.5%' },
  { code: 'PL', name: 'Poland', currency: 'PLN', tax: 'VAT 23%' },
  { code: 'CZ', name: 'Czech Republic', currency: 'CZK', tax: 'DPH 21%' },
  { code: 'GR', name: 'Greece', currency: 'EUR', tax: 'FPA 24%' },
  { code: 'LU', name: 'Luxembourg', currency: 'EUR', tax: 'TVA 17%' },
  { code: 'CA', name: 'Canada', currency: 'CAD', tax: 'GST 5%' },
  { code: 'AU', name: 'Australia', currency: 'AUD', tax: 'GST 10%' },
  { code: 'NZ', name: 'New Zealand', currency: 'NZD', tax: 'GST 15%' },
  { code: 'JP', name: 'Japan', currency: 'JPY', tax: '10%' },
  { code: 'KR', name: 'South Korea', currency: 'KRW', tax: 'VAT 10%' },
  { code: 'IN', name: 'India', currency: 'INR', tax: 'GST 18%' },
  { code: 'BR', name: 'Brazil', currency: 'BRL', tax: 'IVA 26.5%' },
  { code: 'MX', name: 'Mexico', currency: 'MXN', tax: 'IVA 16%' },
  { code: 'ZA', name: 'South Africa', currency: 'ZAR', tax: 'VAT 15%' },
  { code: 'AE', name: 'UAE', currency: 'AED', tax: 'VAT 5%' },
  { code: 'SG', name: 'Singapore', currency: 'SGD', tax: 'GST 9%' },
  { code: 'HK', name: 'Hong Kong', currency: 'HKD', tax: 'No VAT' },
];

const BUSINESS_TYPES = [
  { id: 'personal', label: 'Just me', description: 'Tracking my own finances', icon: User },
  { id: 'freelancer', label: 'Freelancer', description: 'I work for myself', icon: Briefcase },
  { id: 'sole_trader', label: 'Sole Trader', description: 'I run my own shop', icon: Store },
  { id: 'llc', label: 'LLC / Ltd', description: 'Limited liability company', icon: Building2 },
  { id: 'corporation', label: 'Corporation', description: 'Registered company', icon: Users },
  { id: 'nonprofit', label: 'Non-Profit', description: 'Charity or foundation', icon: Heart },
];

const TOTAL_STEPS = 4;

function ProgressBar({ step }) {
  return (
    <div className="bg-white px-6 py-3 border-b border-zinc-100">
      <div className="max-w-lg mx-auto flex gap-2">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <div
            key={`step-${i}`}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              i < step ? 'bg-[#D94E18]' : 'bg-zinc-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function StepWelcome({ userName, companyName, setCompanyName, onNext }) {
  const firstName = userName?.split(' ')[0] || '';

  return (
    <div data-testid="onboarding-step-welcome" className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h2 className="text-2xl font-bold text-zinc-900 mb-1">
        Hey{firstName ? ` ${firstName}` : ''}! Welcome to Kaya
      </h2>
      <p className="text-zinc-400 text-sm mb-8">
        We just need a couple of things to get you set up. Takes about 30 seconds.
      </p>

      <div className="space-y-2">
        <label className="text-sm font-medium text-zinc-700">
          What should we call your business?
        </label>
        <Input
          data-testid="company-name-input"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="e.g. Acme Consulting"
          className="h-12 rounded-xl text-sm"
          autoFocus
        />
        <p className="text-xs text-zinc-400">
          Just doing your own books? Your name works perfectly.
        </p>
      </div>

      <Button
        onClick={onNext}
        disabled={!companyName.trim()}
        data-testid="onboarding-next"
        className="w-full h-12 mt-8 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
      >
        Got it, next <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

function StepCountry({ search, setSearch, selectedCountry, setSelectedCountry, onNext, onBack }) {
  const filteredCountries = search
    ? COUNTRIES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : COUNTRIES;

  return (
    <div data-testid="onboarding-step-country" className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h2 className="text-2xl font-bold text-zinc-900 mb-1">Quick one — where are you based?</h2>
      <p className="text-zinc-400 text-sm mb-6">
        This helps us set up the right currency and tax rules for you.
      </p>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search countries..."
          data-testid="country-search"
          className="w-full h-11 pl-10 pr-4 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D94E18]/20 focus:border-[#D94E18]"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[340px] overflow-y-auto pr-1" data-testid="country-grid">
        {filteredCountries.map(country => (
          <button
            key={country.code}
            onClick={() => setSelectedCountry(country)}
            data-testid={`country-${country.code}`}
            className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
              selectedCountry?.code === country.code
                ? 'border-[#D94E18] bg-[#D94E18]/5 ring-1 ring-[#D94E18]/20'
                : 'border-zinc-200 hover:border-zinc-300 bg-white'
            }`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-800 truncate">{country.name}</p>
              <p className="text-xs text-zinc-400">{country.currency} · {country.tax}</p>
            </div>
            {selectedCountry?.code === country.code && (
              <Check className="w-4 h-4 text-[#D94E18] flex-shrink-0" />
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} data-testid="onboarding-back"
          className="h-12 px-5 rounded-xl font-semibold">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Button
          onClick={onNext}
          disabled={!selectedCountry}
          data-testid="onboarding-next"
          className="flex-1 h-12 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
        >
          Next <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function StepEntityType({ selectedType, setSelectedType, onNext, onBack, saving }) {
  return (
    <div data-testid="onboarding-step-entity" className="animate-in fade-in slide-in-from-right-4 duration-300">
      <h2 className="text-2xl font-bold text-zinc-900 mb-1">Almost there! How do you operate?</h2>
      <p className="text-zinc-400 text-sm mb-6">
        Are you running a company, freelancing, or just tracking personal stuff? We'll tailor things for you.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="entity-type-grid">
        {BUSINESS_TYPES.map(type => {
          const Icon = type.icon;
          return (
            <button
              key={type.id}
              onClick={() => setSelectedType(type.id)}
              data-testid={`entity-${type.id}`}
              className={`flex items-start gap-3.5 p-4 rounded-xl border text-left transition-all ${
                selectedType === type.id
                  ? 'border-[#D94E18] bg-[#D94E18]/5 ring-1 ring-[#D94E18]/20'
                  : 'border-zinc-200 hover:border-zinc-300 bg-white'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                selectedType === type.id ? 'bg-[#D94E18] text-white' : 'bg-zinc-100 text-zinc-500'
              }`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-zinc-800">{type.label}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{type.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3 mt-6">
        <Button variant="outline" onClick={onBack} data-testid="onboarding-back"
          className="h-12 px-5 rounded-xl font-semibold">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Button
          onClick={onNext}
          disabled={!selectedType || saving}
          data-testid="onboarding-next"
          className="flex-1 h-12 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-40"
        >
          {saving ? (
            <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Setting things up...</>
          ) : (
            <>Let's go <ArrowRight className="w-4 h-4" /></>
          )}
        </Button>
      </div>
    </div>
  );
}

const NEXT_STEPS = [
  {
    icon: Upload,
    title: 'Drop in a bank statement',
    description: 'PDF, CSV, or just snap a photo — we handle any format.',
  },
  {
    icon: Sparkles,
    title: 'We do the heavy lifting',
    description: 'Kaya reads every transaction and sorts it into the right account.',
  },
  {
    icon: FileBarChart,
    title: 'Your reports, ready to go',
    description: 'P&L, Balance Sheet — generated in seconds, not hours.',
  },
];

function StepReady({ companyName, onFinish }) {
  return (
    <div data-testid="onboarding-step-ready" className="animate-in fade-in slide-in-from-right-4 duration-300">
      <div className="text-center mb-8">
        <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Check className="w-7 h-7 text-emerald-600" />
        </div>
        <h2 className="text-2xl font-bold text-zinc-900 mb-1">That's it, you're good to go!</h2>
        <p className="text-zinc-400 text-sm">
          {companyName ? `${companyName} is all set up.` : 'Everything is set up.'} Here's how easy it is:
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {NEXT_STEPS.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.title} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-zinc-100">
              <div className="w-10 h-10 bg-[#D94E18]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-[#D94E18]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">{item.title}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{item.description}</p>
              </div>
            </div>
          );
        })}
      </div>

      <Button
        onClick={onFinish}
        data-testid="onboarding-go-to-dashboard"
        className="w-full h-12 bg-[#D94E18] hover:bg-[#C2410C] text-white font-semibold rounded-xl flex items-center justify-center gap-2"
      >
        Take me to my dashboard <ArrowRight className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function OnboardingPage() {
  const { isAuthenticated, user } = useAuth();
  const { refreshOrg } = useOrg();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [companyName, setCompanyName] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (!isAuthenticated) return null;

  const handleSaveAndContinue = async () => {
    setSaving(true);
    try {
      if (selectedCountry) {
        await organizationAPI.changeCountry(selectedCountry.code);
      }
      const updates = {};
      if (companyName.trim()) updates.company_name = companyName.trim();
      if (selectedType) updates.entity_type = selectedType;
      updates.onboarding_complete = true;
      await organizationAPI.update(updates);
      await refreshOrg();
      setStep(4);
    } catch (err) {
      toast.error('Something went wrong. You can update these later in Settings.');
      await refreshOrg();
      setStep(4);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await organizationAPI.update({ onboarding_complete: true });
      await refreshOrg();
    } catch {}
    navigate('/dashboard', { replace: true });
  };

  const handleFinish = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-[#D94E18] rounded-lg flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" />
              <rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" />
            </svg>
          </div>
          <span className="text-lg font-bold text-zinc-900">kaya.ai</span>
        </div>
        {step < 4 && (
          <button
            onClick={handleSkip}
            className="text-sm text-zinc-400 hover:text-zinc-600 transition-colors"
            data-testid="onboarding-skip"
          >
            Skip for now
          </button>
        )}
      </div>

      <ProgressBar step={step} />

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-6 py-10">
        <div className="w-full max-w-lg">
          {step === 1 && (
            <StepWelcome
              userName={user?.name}
              companyName={companyName}
              setCompanyName={setCompanyName}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <StepCountry
              search={search}
              setSearch={setSearch}
              selectedCountry={selectedCountry}
              setSelectedCountry={setSelectedCountry}
              onNext={() => setStep(3)}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && (
            <StepEntityType
              selectedType={selectedType}
              setSelectedType={setSelectedType}
              onNext={handleSaveAndContinue}
              onBack={() => setStep(2)}
              saving={saving}
            />
          )}
          {step === 4 && (
            <StepReady
              companyName={companyName}
              onFinish={handleFinish}
            />
          )}
        </div>
      </div>
    </div>
  );
}
